"use client";

import { Kbd, KbdGroup } from "@workspace/ui/components/kbd";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@workspace/ui/components/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@workspace/ui/components/tooltip";
import { Settings } from "lucide-react";
import type { Route } from "next";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect } from "react";

type SidebarSettingsShortcutProps = {
  orgSlug: string;
};

const SHORTCUT_RESET_MS = 1200;

function isEditableEventTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName.toLowerCase();
  return (
    tagName === "input" ||
    tagName === "textarea" ||
    tagName === "select" ||
    target.isContentEditable
  );
}

export function SidebarSettingsShortcut({
  orgSlug,
}: SidebarSettingsShortcutProps) {
  const pathname = usePathname();
  const router = useRouter();
  const configBasePath = `/${orgSlug}/configuracion`;
  const isInConfiguration =
    pathname === configBasePath || pathname.startsWith(`${configBasePath}/`);

  useEffect(() => {
    if (isInConfiguration) {
      return;
    }

    let pendingGoTo = false;
    let resetTimeoutId: number | undefined;

    const resetPendingShortcut = () => {
      pendingGoTo = false;
      if (resetTimeoutId) {
        window.clearTimeout(resetTimeoutId);
        resetTimeoutId = undefined;
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        event.isComposing ||
        isEditableEventTarget(event.target)
      ) {
        return;
      }

      const key = event.key.toLowerCase();
      if (key === "g") {
        pendingGoTo = true;
        if (resetTimeoutId) {
          window.clearTimeout(resetTimeoutId);
        }
        resetTimeoutId = window.setTimeout(
          resetPendingShortcut,
          SHORTCUT_RESET_MS
        );
        return;
      }

      if (!pendingGoTo) {
        return;
      }

      resetPendingShortcut();

      if (key !== "s") {
        return;
      }

      event.preventDefault();
      router.push(configBasePath as Route);
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      resetPendingShortcut();
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [configBasePath, isInConfiguration, router]);

  if (isInConfiguration) {
    return null;
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <Tooltip>
          <TooltipTrigger
            render={
              <SidebarMenuButton
                render={<Link href={`/${orgSlug}/configuracion`} />}
              />
            }
          >
            <Settings />
            <span>Configuración</span>
          </TooltipTrigger>
          <TooltipContent align="center" side="right">
            Configuración
            <KbdGroup>
              <Kbd>G</Kbd>
              <span className="font-sans text-muted text-xs">luego</span>
              <Kbd>S</Kbd>
            </KbdGroup>
          </TooltipContent>
        </Tooltip>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
